import { useState } from "react";
import { Sparkles, Loader2, Copy, Check } from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Badge } from "../../ui/badge";

const SECTORS = [
  "business", "fitness", "e-commerce", "food", "fashion", "tech",
  "travel", "education", "beauty", "real-estate", "automotive", "entertainment",
];

const COUNTRIES = [
  { code: "BE", label: "België" },
  { code: "NL", label: "Nederland" },
  { code: "FR", label: "Frankrijk" },
  { code: "DE", label: "Duitsland" },
  { code: "GB", label: "Verenigd Koninkrijk" },
  { code: "US", label: "Verenigde Staten" },
];

const STYLES = [
  { key: "", label: "Variatie (alle stijlen)" },
  { key: "vraag", label: "Vraag" },
  { key: "cijfer", label: "Cijfer" },
  { key: "contrair", label: "Contrair" },
  { key: "curiosity", label: "Curiosity" },
  { key: "story", label: "Story" },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);

const HookGeneratorDialog = ({ open, onOpenChange, generateHooks, isDark }) => {
  const [form, setForm] = useState({
    sector: "",
    country: "",
    target_audience: "",
    style: "",
    count: 8,
  });
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const handleGenerate = async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await generateHooks({
        sector: form.sector || null,
        country: form.country || null,
        target_audience: form.target_audience || null,
        style: form.style || null,
        count: Number(form.count) || 8,
      });
      setHooks(Array.isArray(res?.hooks) ? res.hooks : []);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Genereren mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (next) => {
    if (!next) {
      setHooks([]);
      setErr(null);
    }
    onOpenChange(next);
  };

  const copyHook = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch (_e) {
      // ignore
    }
  };

  const scoreColor = (score) => {
    if (score >= 75) return isDark ? "text-emerald-400" : "text-emerald-600";
    if (score >= 50) return isDark ? "text-yellow-400" : "text-yellow-600";
    return isDark ? "text-red-400" : "text-red-600";
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Hook Generator
          </DialogTitle>
          <DialogDescription>
            Claude genereert hook-varianten gebaseerd op je top performers + de gevraagde stijl.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sector">
              <Select
                value={form.sector || "none"}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, sector: v === "none" ? "" : v }))
                }
              >
                <SelectTrigger><SelectValue placeholder="Kies sector" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Algemeen</SelectItem>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Land">
              <Select
                value={form.country || "none"}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, country: v === "none" ? "" : v }))
                }
              >
                <SelectTrigger><SelectValue placeholder="Kies land" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Doelgroep">
            <Input
              value={form.target_audience}
              onChange={(e) =>
                setForm((s) => ({ ...s, target_audience: e.target.value }))
              }
              placeholder="bv. 'Jonge ondernemers, 25-35'"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Stijl">
              <Select
                value={form.style || "any"}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, style: v === "any" ? "" : v }))
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STYLES.map((s) => (
                    <SelectItem key={s.key || "any"} value={s.key || "any"}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Aantal (1-20)">
              <Input
                type="number"
                min="1"
                max="20"
                value={form.count}
                onChange={(e) =>
                  setForm((s) => ({ ...s, count: e.target.value }))
                }
              />
            </Field>
          </div>
        </div>

        {err && (
          <div
            className={cn(
              "rounded-md border p-2 text-[12px]",
              isDark ? "bg-red-500/[0.08] border-red-500/20 text-red-300" : "bg-red-50 border-red-200 text-red-700"
            )}
          >
            {err}
          </div>
        )}

        {hooks.length > 0 && (
          <div className="space-y-2">
            <p
              className={cn(
                "text-[11px] font-semibold uppercase tracking-wider",
                isDark ? "text-gray-400" : "text-gray-500"
              )}
            >
              {hooks.length} hooks gegenereerd
            </p>
            <div className="grid grid-cols-1 gap-2">
              {hooks.map((h, i) => (
                <div
                  key={i}
                  className={cn(
                    "rounded-lg border p-3 flex items-start justify-between gap-3",
                    isDark
                      ? "bg-white/[0.02] border-white/[0.06]"
                      : "bg-gray-50/50 border-gray-200"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "text-[13px] font-medium",
                        isDark ? "text-gray-200" : "text-gray-800"
                      )}
                    >
                      {h.text}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      {h.style && (
                        <Badge variant="outline" className="h-[18px] text-[10px] px-1.5">
                          {h.style}
                        </Badge>
                      )}
                      {typeof h.predicted_score === "number" && (
                        <span className={cn("text-[11px] font-semibold", scoreColor(h.predicted_score))}>
                          {Math.round(h.predicted_score)} / 100
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyHook(h.text, i)}
                    className="h-7 px-2 gap-1 text-[11px] flex-shrink-0"
                  >
                    {copiedIdx === i ? (
                      <>
                        <Check className="h-3 w-3 text-emerald-500" />
                        Gekopieerd
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        Kopieer
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Sluiten
          </Button>
          <Button onClick={handleGenerate} disabled={loading}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {hooks.length > 0 ? "Opnieuw genereren" : "Genereer hooks"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HookGeneratorDialog;
